<template>
  <div class="page-mutate-role">
    <Tabs v-model="type" :items="[{ id: 'page', label: $tc('page') }]" theme="border">
      <template #page>
        <div class="page-mutate-role__input p-4">
          <InputComponent :label="labels.manager" :sublabel="$t('canEdit')">
            <UserSelect
              :value="getRole('manager')"
              @input="$ev => setRole('manager', $ev)"
              multiple
              search
              :items="$ev => fetchUsers($ev, { type: 'user' })"
              :promise="promise"
              :title="$t('selectx', { x: $tc('projectmanager', 2).toLowerCase() })"
              class="elder-input__element"
            />
          </InputComponent>
          <InputComponent :label="labels.salesman" :sublabel="$t('helpers:roleSalesman')">
            <template #default>
              <UserSelect
                :value="getRole('salesman', { sort: false })"
                @input="$ev => setRole('salesman', $ev)"
                multiple
                search
                :items="fetchUsers"
                :promise="promise"
                sortable
                :title="$t('selectx', { x: $tc('customermanager', 2).toLowerCase() })"
                class="elder-input__element"
              />
            </template>
            <template #after-label>
              <HelperPopover template="salesman-page-helper" />
            </template>
          </InputComponent>
        </div>
      </template>
    </Tabs>

    <CustomFields
      v-model="value.customFields"
      :schema="customFieldSchemas"
      :data="data"
      :placement="['roles', 'page:roles']"
      :validator="validator"
      validator-prefix="customFields"
      :disabledFields="$path('metadata.disabledFields.page', item)"
    />
  </div>
</template>

<script>
import UserApi from '@/api/User'
import { mapActions, mapState } from 'vuex'
import { mergeDeepRight, path, assocPath, uniqBy } from 'ramda'
import { Clone, SortByKey } from '@/utils'
import { i18n } from '@/i18n'

import { InputComponent } from 'vue-elder-input'
import UserSelect from '@/components/User/Select/Modal'
import CustomFields from '@kvass/custom-fields'
import Tabs from '@/components/Tabs'
import HelperPopover from '@/components/HelperPopover/Component'
import Bus from '@/bus'

const meta = {
  order: 1001,
  id: 'roles',
  label: i18n.tc('role', 2),
  icon: ['fal', 'user'],
}

const getState = key => {
  return state => {
    return path(key.split('.'), state)
  }
}

export { meta }
export default {
  props: {
    data: Object,
    value: Object,
    labels: Object,
    validator: Object,
    customFieldSchemas: { type: Array, default: () => [] },
  },
  data() {
    return {
      promise: null,
      type: 'page',
    }
  },
  computed: {
    ...mapState('Page', {
      id: getState('item.id'),
      item(state) {
        return state.item && Object.keys(state.item).length ? state.item : this.data
      },
    }),
    draft() {
      return this.$path('item.draftId') || this.$path('item.metadata.draftAvailable')
    },
  },
  methods: {
    ...mapActions('Page', ['mutate']),
    fetchUsers(innerFilter = {}, outerFilter = {}) {
      return UserApi.get({ filter: { ...innerFilter, ...outerFilter } }, { query: 'UserSearch' })
    },
    getRole(role, options = {}) {
      const { sort = 'name' } = options
      role = role instanceof Array ? role : [role]
      const items = Clone(uniqBy(i => i && i.id, role.map(r => this.$path('item.roles.' + r) || []).flat()))

      if (sort) return items.sort(SortByKey(sort))

      return items
    },
    hasRole(role, value) {
      if (role instanceof Array) return role.some(r => this.hasRole(r, value))
      return Boolean((this.$path('item.roles.' + role) || []).find(r => r.id === value.id))
    },
    setRole(role, value, soft) {
      role = role instanceof Array ? role : [role]

      let getData = cur => {
        let rest = role.filter(r => r !== cur)

        return value.filter(v => !soft || !this.hasRole(rest, v) || this.hasRole(cur, v))
      }

      this.item.roles[role] = getData(role) // Set value in state to prevent sort glitch

      this.promise = this.mutate([
        {
          id: this.id,
          data: {
            roles: role.reduce((res, cur) => {
              return assocPath(
                cur.split('.'),
                getData(cur).map(v => v.id),
                res,
              )
            }, {}),
          },
          getConfig: false,
        },
        {
          transformPayload: false,
          strategy: (item, payload) => {
            return mergeDeepRight(item, {
              draftId: payload.draftId,
              roles: role.reduce((res, cur) => {
                return assocPath(cur.split('.'), getData(cur), res)
              }, {}),
            })
          },
          draft: this.draft,
        },
      ]).then(v => {
        Bus.$emit('draft:update', v.draftId)
      })
    },
  },
  components: {
    InputComponent,
    UserSelect,
    CustomFields,
    Tabs,
    HelperPopover,
  },
}
</script>

<style lang="scss">
.page-mutate-role {
  max-width: 800px;

  &__input {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    grid-gap: 1rem 2rem;
  }

  .elder-input__element {
    max-height: 300px;
    overflow-y: auto;

    @include scrollShadow();
  }

  .select-modal__input {
    .card__content {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 0.5rem;
    }

    .user-card__custom {
      margin-left: auto;
    }
  }

  .tabs {
    &__details {
      border: none !important;
      box-shadow: var(--box-shadow);
    }

    &__header {
      ul,
      a {
        border: none !important;
      }

      a {
        background-color: rgba(black, 0.03);
      }

      &--active a {
        background-color: White;
      }
    }
  }
}
</style>
